'use client';

import { useState, useEffect, useRef } from 'react';
import { Calendar, Users, Phone, Mail, CalendarDays, DollarSign, CheckCircle, Clock, MapPin, Star, Heart, Gift, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DayPicker } from 'react-day-picker';
import { format, isBefore, addDays, isAfter, isSameDay } from 'date-fns';
import { supabase } from '@/lib/supabase';
import 'react-day-picker/dist/style.css';

// Type definitions for calendar data
interface CalendarSetting {
  date: string;
  base_price: string;
  is_available: boolean;
  is_reserved: boolean;
  notes: string;
}

interface CalendarBooking {
  id: string;
  check_in: string;
  check_out: string;
  status: string;
}

interface BlockedDate {
  id: string;
  start_date: string;
  end_date: string;
  reason?: string;
}

interface CalendarData {
  bookings: CalendarBooking[];
  settings: CalendarSetting[];
  blockedDates: BlockedDate[];
}

const Booking = (): JSX.Element => {
  const [isVisible, setIsVisible] = useState(false);
  const [checkIn, setCheckIn] = useState<Date | undefined>();
  const [checkOut, setCheckOut] = useState<Date | undefined>();
  const [totalPrice, setTotalPrice] = useState(0);
  const [nights, setNights] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [calendarData, setCalendarData] = useState<CalendarData>({
    bookings: [],
    settings: [],
    blockedDates: []
  });
  const [isLoadingCalendar, setIsLoadingCalendar] = useState(true);
  const [dateError, setDateError] = useState('');
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkOutOpen, setCheckOutOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Load calendar data
  useEffect(() => {
    loadCalendarData();
  }, []);

  const loadCalendarData = async () => {
    try {
      setIsLoadingCalendar(true);
      
      // Load bookings
      const { data: bookings } = await supabase
        .from('bookings')
        .select('*')
        .neq('status', 'cancelled');

      // Load calendar settings
      const { data: settings } = await supabase
        .from('calendar_settings')
        .select('*');

      // Load blocked dates
      const { data: blockedDates } = await supabase
        .from('blocked_dates')
        .select('*');

      setCalendarData({
        bookings: bookings || [],
        settings: settings || [],
        blockedDates: blockedDates || []
      });
    } catch (error) {
      console.error('Error loading calendar data:', error);
    } finally {
      setIsLoadingCalendar(false);
    }
  };

  const getDateInfo = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    
    const booking = calendarData.bookings.find((b) => 
      dateStr >= b.check_in && dateStr < b.check_out && b.status !== 'cancelled'
    );
    
    const setting = calendarData.settings.find((s) => s.date === dateStr);
    
    const blocked = calendarData.blockedDates.find((b) => 
      dateStr >= b.start_date && dateStr <= b.end_date
    );

    return { booking, setting, blocked };
  };

  const isDateUnavailable = (date: Date) => {
    // Don't allow past dates
    if (isBefore(date, new Date())) {
      return true;
    }

    const { booking, setting, blocked } = getDateInfo(date);
    
    // Date is unavailable if it's booked, blocked, reserved, or marked as unavailable
    return !!(booking || blocked || setting?.is_reserved || (setting && !setting.is_available));
  };

  const isDateReserved = (date: Date) => {
    const { setting } = getDateInfo(date);
    return !!(setting?.is_reserved);
  };

  const isDateBooked = (date: Date) => {
    const { booking } = getDateInfo(date);
    return !!booking;
  };

  const isDateBlocked = (date: Date) => {
    const { blocked } = getDateInfo(date);
    return !!blocked;
  };

  // Calculate price and validate date range
  useEffect(() => {
    if (checkIn && checkOut) {
      setDateError('');
      
      if (isBefore(checkOut, checkIn) || isSameDay(checkIn, checkOut)) {
        setDateError('Check-out date must be after check-in date');
        setNights(0);
        setTotalPrice(0);
        return;
      }

      // Check if any date in the range is unavailable
      let currentDate = new Date(checkIn);
      let totalCost = 0;
      let nightCount = 0;
      let hasUnavailableDate = false;

      while (isBefore(currentDate, checkOut)) {
        if (isDateUnavailable(currentDate)) {
          hasUnavailableDate = true;
          break;
        }

        const { setting } = getDateInfo(currentDate);
        const dayPrice = setting?.base_price ? parseFloat(setting.base_price) : 350;
        totalCost += dayPrice;
        nightCount++;
        
        currentDate = addDays(currentDate, 1);
      }

      if (hasUnavailableDate) {
        setDateError('Some dates in your selected range are not available. Please choose different dates.');
        setNights(0);
        setTotalPrice(0);
      } else {
        setNights(nightCount);
        setTotalPrice(totalCost);
      }
    } else {
      setNights(0);
      setTotalPrice(0);
      setDateError('');
    }
  }, [checkIn, checkOut, calendarData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('starting');
    if (dateError || nights === 0 || totalPrice === 0) {
      setDateError('Please select valid available dates for your stay.');
      return;
    }
    
    // Send booking email
    sendBookingEmail();
  };

  const sendBookingEmail = async () => {
    const requestId = `booking_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    console.log(`[${new Date().toISOString()}] [${requestId}] Starting booking email process`);
    
    try {
      console.log(`[${new Date().toISOString()}] [${requestId}] Getting user session...`);
      // Get the current user session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.log(`[${new Date().toISOString()}] [${requestId}] Session error:`, sessionError.message);
      }
      
      if (!session) {
        console.log(`[${new Date().toISOString()}] [${requestId}] No user session found, proceeding without auth`);
      } else {
        console.log(`[${new Date().toISOString()}] [${requestId}] User session found:`, {
          hasAccessToken: !!session.access_token,
          tokenLength: session.access_token?.length || 0,
          userEmail: session.user?.email,
          expiresAt: session.expires_at
        });
      }

      console.log(`[${new Date().toISOString()}] [${requestId}] Preparing booking data...`);
      const bookingData: {
        bookingId: string;
        guestName: string;
        guestEmail: string;
        guestPhone: string;
        checkIn: string;
        checkOut: string;
        totalPrice: string;
        specialRequests: string;
        type: 'new_booking';
      } = {
        bookingId: `BK${Date.now()}`, // Generate a booking ID
        guestName: formData.name,
        guestEmail: formData.email,
        guestPhone: formData.phone,
        checkIn: format(checkIn!, 'yyyy-MM-dd'),
        checkOut: format(checkOut!, 'yyyy-MM-dd'),
        totalPrice: totalPrice.toString(),
        specialRequests: formData.message,
        type: 'new_booking'
      };
      
      console.log(`[${new Date().toISOString()}] [${requestId}] Booking data prepared:`, {
        bookingId: bookingData.bookingId,
        guestName: bookingData.guestName,
        guestEmail: bookingData.guestEmail,
        guestPhone: bookingData.guestPhone,
        checkIn: bookingData.checkIn,
        checkOut: bookingData.checkOut,
        totalPrice: bookingData.totalPrice,
        hasSpecialRequests: !!bookingData.specialRequests,
        type: bookingData.type
      });

      console.log(`[${new Date().toISOString()}] [${requestId}] Preparing request headers...`);
      // Prepare headers with optional authentication
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY || ''}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
      };
      
      console.log(`[${new Date().toISOString()}] [${requestId}] Initial headers prepared:`, {
        'Content-Type': headers['Content-Type'],
        'Authorization': `Bearer ${(import.meta.env.VITE_SUPABASE_ANON_KEY || '').substring(0, 20)}...`,
        'apikey': `${(import.meta.env.VITE_SUPABASE_ANON_KEY || '').substring(0, 20)}...`,
        hasAnonKey: !!import.meta.env.VITE_SUPABASE_ANON_KEY
      });
      
      // Override with user token if we have a session
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
        console.log(`[${new Date().toISOString()}] [${requestId}] Adding auth header with user token:`, {
          tokenLength: session.access_token.length,
          tokenStart: session.access_token.substring(0, 20)
        });
      } else {
        console.log(`[${new Date().toISOString()}] [${requestId}] Using anon key for authentication`);
      }
      
      console.log(`[${new Date().toISOString()}] [${requestId}] Final headers:`, {
        'Content-Type': headers['Content-Type'],
        'Authorization': `Bearer ${(headers['Authorization'] || '').substring(7, 27)}...`,
        'apikey': `${(headers['apikey'] || '').substring(0, 20)}...`
      });
      
      const requestUrl = `${import.meta.env.VITE_SUPABASE_URL || ''}/functions/v1/send-booking-email`;
      console.log(`[${new Date().toISOString()}] [${requestId}] Request URL:`, requestUrl);
      console.log(`[${new Date().toISOString()}] [${requestId}] Environment variables:`, {
        VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL || '',
        hasAnonKey: !!import.meta.env.VITE_SUPABASE_ANON_KEY
      });
      
      const requestBody = JSON.stringify(bookingData);
      console.log(`[${new Date().toISOString()}] [${requestId}] Request body:`, requestBody);
      console.log(`[${new Date().toISOString()}] [${requestId}] Request body length:`, requestBody.length);

      console.log(`[${new Date().toISOString()}] [${requestId}] Making fetch request...`);
      const fetchStartTime = Date.now();
      
      const response = await fetch(requestUrl, {
        method: 'POST',
        headers,
        body: requestBody,
      });
      
      const fetchEndTime = Date.now();
      const fetchDuration = fetchEndTime - fetchStartTime;
      
      console.log(`[${new Date().toISOString()}] [${requestId}] Fetch completed in ${fetchDuration}ms`);
      console.log(`[${new Date().toISOString()}] [${requestId}] Response received:`, {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        type: response.type,
        url: response.url,
        redirected: response.redirected
      });

      console.log(`[${new Date().toISOString()}] [${requestId}] Response headers:`);
      const responseHeaders: Record<string, string> = Object.fromEntries(response.headers.entries());
      for (const [key, value] of Object.entries(responseHeaders)) {
        console.log(`[${new Date().toISOString()}] [${requestId}]   ${key}: ${value}`);
      }
      
      console.log(`[${new Date().toISOString()}] [${requestId}] Reading response body...`);
      const responseText = await response.text();
      console.log(`[${new Date().toISOString()}] [${requestId}] Response body (length: ${responseText.length}):`, responseText);
      
      console.log(`[${new Date().toISOString()}] [${requestId}] Attempting to parse response as JSON...`);
      let responseData: any;
      try {
        responseData = JSON.parse(responseText);
        console.log(`[${new Date().toISOString()}] [${requestId}] Successfully parsed response data:`, responseData);
      } catch (parseError: unknown) {
        const errorMessage = parseError instanceof Error ? parseError.message : 'Unknown parsing error';
        console.log(`[${new Date().toISOString()}] [${requestId}] Response is not valid JSON:`, errorMessage);
        console.log(`[${new Date().toISOString()}] [${requestId}] Raw response text:`, responseText);
      }
      
      console.log(`[${new Date().toISOString()}] [${requestId}] Checking response status...`);
      if (response.ok) {
        console.log(`[${new Date().toISOString()}] [${requestId}] Response OK - showing success message`);
        setIsSubmitted(true);
        setTimeout(() => setIsSubmitted(false), 8000);
      } else {
        console.error(`[${new Date().toISOString()}] [${requestId}] Failed to send booking email`);
        console.error(`[${new Date().toISOString()}] [${requestId}] Error details:`, { 
          status: response.status, 
          statusText: response.statusText,
          body: responseText,
          headers: responseHeaders
        });
        // Still show success to user, but log the error
        console.log(`[${new Date().toISOString()}] [${requestId}] Showing success message despite error`);
        setIsSubmitted(true);
        setTimeout(() => setIsSubmitted(false), 8000);
      }
      
      console.log(`[${new Date().toISOString()}] [${requestId}] Booking email process completed successfully`);
    } catch (error: unknown) {
      console.error(`[${new Date().toISOString()}] [${requestId}] Error sending booking email:`, error);
      
      if (error instanceof Error) {
        console.error(`[${new Date().toISOString()}] [${requestId}] Error name:`, error.name);
        console.error(`[${new Date().toISOString()}] [${requestId}] Error message:`, error.message);
        console.error(`[${new Date().toISOString()}] [${requestId}] Error stack:`, error.stack);
      } else {
        console.error(`[${new Date().toISOString()}] [${requestId}] Unknown error type:`, typeof error);
      }
      
      if (error instanceof TypeError) {
        console.error(`[${new Date().toISOString()}] [${requestId}] Network error - check if edge function is accessible`);
      }
      
      // Still show success to user, but log the error
      console.log(`[${new Date().toISOString()}] [${requestId}] Showing success message despite error`);
      setIsSubmitted(true);
      setTimeout(() => setIsSubmitted(false), 8000);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const contactInfo = [
    { icon: Phone, label: 'Phone', value: '718-517-9235', href: 'tel:718-517-9235' },
    { icon: Mail, label: 'Email', value: 'dira4ubp@gmail.com', href: 'mailto:dira4ubp@gmail.com' }
  ];

  const highlights = [
    { icon: Star, text: "5-Star Luxury Experience" },
    { icon: MapPin, text: "Prime Monroe Location" },
    { icon: Heart, text: "Family-Friendly Environment" },
    { icon: Gift, text: "Special Amenities Included" }
  ];

  // Custom styles for DayPicker
  const dayPickerStyles: Record<string, React.CSSProperties> = {
    caption: { color: '#333333', fontWeight: 'bold', marginBottom: '1rem' },
    day: { 
      borderRadius: '8px',
      transition: 'all 0.2s ease',
    },
    day_selected: { 
      backgroundColor: '#7C8F75',
      color: 'white',
      fontWeight: 'bold'
    },
    day_today: { 
      backgroundColor: '#A8B89A',
      color: 'white',
      fontWeight: 'bold'
    },
    day_disabled: { 
      backgroundColor: '#f3f4f6',
      color: '#9ca3af',
      textDecoration: 'line-through'
    },
    day_outside: { 
      color: '#d1d5db'
    }
  };

  const modifiers = {
    booked: (date: Date) => isDateBooked(date),
    blocked: (date: Date) => isDateBlocked(date),
    reserved: (date: Date) => isDateReserved(date),
    unavailable: (date: Date) => isDateUnavailable(date),
  };

  const modifiersStyles: Record<string, React.CSSProperties> = {
    booked: { 
      backgroundColor: '#fee2e2', 
      color: '#991b1b',
      textDecoration: 'line-through',
      cursor: 'not-allowed'
    },
    blocked: { 
      backgroundColor: '#fef3c7', 
      color: '#92400e',
      textDecoration: 'line-through',
      cursor: 'not-allowed'
    },
    reserved: { 
      backgroundColor: '#e0e7ff', 
      color: '#3730a3',
      fontWeight: 'bold',
      textDecoration: 'line-through',
      cursor: 'not-allowed',
      border: '2px solid #6366f1'
    },
    unavailable: {
      cursor: 'not-allowed',
      opacity: 0.5
    },
  };

  return (
    <section id="booking" ref={sectionRef} className="py-20 bg-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 rounded-full bg-gradient-to-br from-[#7C8F75]/10 to-[#A8B89A]/5 blur-2xl animate-float"></div>
        <div className="absolute bottom-32 right-16 w-40 h-40 rounded-full bg-gradient-to-tl from-[#A8B89A]/8 to-[#7C8F75]/4 blur-3xl animate-pulse"></div>
        <div className="absolute top-1/2 left-1/3 w-24 h-24 rounded-full bg-gradient-to-r from-[#7C8F75]/6 to-transparent blur-xl animate-bounce"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`transform transition-all duration-1000 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold text-[#333333] mb-6 relative">
              <span className="bg-gradient-to-r from-[#7C8F75] via-[#A8B89A] to-[#7C8F75] bg-clip-text text-transparent">
                Reserve Your Stay
              </span>
              <div className="absolute -bottom-3 sm:-bottom-4 left-1/2 transform -translate-x-1/2 w-24 sm:w-32 h-1 bg-gradient-to-r from-transparent via-[#7C8F75] to-transparent"></div>
            </h2>
            <p className="text-lg sm:text-xl text-[#333333]/80 max-w-3xl mx-auto leading-relaxed px-4">
              Plan your stay at Mountain View. Enjoy comfortable accommodations in a convenient Monroe, NY location.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Booking Form - Takes 2 columns */}
            <div className="lg:col-span-2">
              <div className="bg-gradient-to-br from-[#F9F6F2] via-white to-[#F9F6F2] p-6 sm:p-8 lg:p-12 rounded-3xl shadow-2xl border border-[#7C8F75]/10 relative overflow-hidden">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-20 h-20 sm:w-32 sm:h-32 bg-gradient-to-bl from-[#7C8F75]/5 to-transparent rounded-bl-full"></div>
                <div className="absolute bottom-0 left-0 w-16 h-16 sm:w-24 sm:h-24 bg-gradient-to-tr from-[#A8B89A]/5 to-transparent rounded-tr-full"></div>

                <div className="relative z-10">
                  <div className="flex items-center space-x-3 sm:space-x-4 mb-6 sm:mb-8">
                    <div className="bg-gradient-to-r from-[#7C8F75] to-[#A8B89A] p-3 rounded-2xl shadow-lg">
                      <CalendarDays className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl sm:text-3xl font-bold text-[#333333]">Booking Details</h3>
                      <p className="text-sm sm:text-base text-[#333333]/70">Plan your perfect getaway</p>
                    </div>
                  </div>

                  {isSubmitted ? (
                    <div className="text-center py-12 sm:py-16">
                      <div className="bg-gradient-to-r from-green-500 to-emerald-500 w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-lg">
                        <CheckCircle className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
                      </div>
                      <h4 className="text-2xl sm:text-3xl font-bold text-[#333333] mb-4">Booking Request Submitted!</h4>
                      <p className="text-base sm:text-lg text-[#333333]/80 mb-4 sm:mb-6 px-4">Thank you for choosing Mountain View. We'll contact you within 24 hours to confirm your reservation.</p>
                      <div className="bg-[#7C8F75]/10 p-4 rounded-xl inline-block">
                        <p className="text-sm sm:text-base text-[#333333] font-medium">Confirmation details will be sent to your email</p>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
                      {/* Date Selection with Calendar Picker */}
                      <div className="bg-white/70 p-4 sm:p-6 rounded-2xl border border-[#7C8F75]/20 shadow-sm">
                        <h4 className="text-lg sm:text-xl font-semibold text-[#333333] mb-4 flex items-center">
                          <Calendar className="h-5 w-5 mr-2 text-[#7C8F75]" />
                          Select Your Dates
                        </h4>
                        
                        {isLoadingCalendar && (
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#7C8F75]"></div>
                            <span className="ml-3 text-[#333333]/70">Loading availability...</span>
                          </div>
                        )}

                        {!isLoadingCalendar && (
                          <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4">
                              <div className="space-y-2">
                                <Label htmlFor="checkin" className="text-[#333333] font-medium flex items-center">
                                  <Clock className="h-4 w-4 mr-2 text-[#7C8F75]" />
                                  Check-in Date
                                </Label>
                                <Popover open={checkInOpen} onOpenChange={setCheckInOpen}>
                                  <PopoverTrigger asChild>
                                    <Button
                                      variant="outline"
                                      className="w-full h-12 justify-start text-left font-normal border-[#7C8F75]/30 focus:border-[#7C8F75] focus:ring-[#7C8F75]/20 rounded-xl"
                                    >
                                      <Calendar className="mr-2 h-4 w-4" />
                                      {checkIn ? format(checkIn, 'PPP') : 'Select check-in date'}
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-auto p-0" align="start">
                                    <DayPicker
                                      mode="single"
                                      selected={checkIn}
                                      onSelect={(date) => {
                                        setCheckIn(date);
                                        setCheckInOpen(false);
                                      }}
                                      disabled={isDateUnavailable}
                                      modifiers={modifiers}
                                      modifiersStyles={modifiersStyles}
                                      styles={dayPickerStyles}
                                      className="p-3"
                                    />
                                    <div className="p-3 border-t border-gray-200 bg-gray-50 text-xs">
                                      <div className="grid grid-cols-2 gap-2">
                                        <div className="flex items-center">
                                          <div className="w-3 h-3 bg-red-200 rounded mr-2"></div>
                                          <span>Booked</span>
                                        </div>
                                        <div className="flex items-center">
                                          <div className="w-3 h-3 bg-yellow-200 rounded mr-2"></div>
                                          <span>Blocked</span>
                                        </div>
                                        <div className="flex items-center">
                                          <div className="w-3 h-3 bg-indigo-200 rounded mr-2 border border-indigo-400"></div>
                                          <span>Reserved</span>
                                        </div>
                                        <div className="flex items-center">
                                          <div className="w-3 h-3 bg-[#7C8F75] rounded mr-2"></div>
                                          <span>Available</span>
                                        </div>
                                      </div>
                                    </div>
                                  </PopoverContent>
                                </Popover>
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="checkout" className="text-[#333333] font-medium flex items-center">
                                  <Clock className="h-4 w-4 mr-2 text-[#7C8F75]" />
                                  Check-out Date
                                </Label>
                                <Popover open={checkOutOpen} onOpenChange={setCheckOutOpen}>
                                  <PopoverTrigger asChild>
                                    <Button
                                      variant="outline"
                                      className="w-full h-12 justify-start text-left font-normal border-[#7C8F75]/30 focus:border-[#7C8F75] focus:ring-[#7C8F75]/20 rounded-xl"
                                      disabled={!checkIn}
                                    >
                                      <Calendar className="mr-2 h-4 w-4" />
                                      {checkOut ? format(checkOut, 'PPP') : 'Select check-out date'}
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-auto p-0" align="start">
                                    <DayPicker
                                      mode="single"
                                      selected={checkOut}
                                      onSelect={(date) => {
                                        setCheckOut(date);
                                        setCheckOutOpen(false);
                                      }}
                                      disabled={(date) => {
                                        if (!checkIn) return true;
                                        return isDateUnavailable(date) || !isAfter(date, checkIn);
                                      }}
                                      modifiers={modifiers}
                                      modifiersStyles={modifiersStyles}
                                      styles={dayPickerStyles}
                                      className="p-3"
                                    />
                                    <div className="p-3 border-t border-gray-200 bg-gray-50 text-xs">
                                      <div className="grid grid-cols-2 gap-2">
                                        <div className="flex items-center">
                                          <div className="w-3 h-3 bg-red-200 rounded mr-2"></div>
                                          <span>Booked</span>
                                        </div>
                                        <div className="flex items-center">
                                          <div className="w-3 h-3 bg-yellow-200 rounded mr-2"></div>
                                          <span>Blocked</span>
                                        </div>
                                        <div className="flex items-center">
                                          <div className="w-3 h-3 bg-indigo-200 rounded mr-2 border border-indigo-400"></div>
                                          <span>Reserved</span>
                                        </div>
                                        <div className="flex items-center">
                                          <div className="w-3 h-3 bg-[#7C8F75] rounded mr-2"></div>
                                          <span>Available</span>
                                        </div>
                                      </div>
                                    </div>
                                  </PopoverContent>
                                </Popover>
                              </div>
                            </div>

                            {/* Date Error Display */}
                            {dateError && (
                              <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <AlertTriangle className="h-5 w-5 text-red-600" />
                                <span className="text-red-700 text-sm">{dateError}</span>
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      {/* Contact Information */}
                      <div className="bg-white/70 p-4 sm:p-6 rounded-2xl border border-[#7C8F75]/20 shadow-sm">
                        <h4 className="text-lg sm:text-xl font-semibold text-[#333333] mb-4 flex items-center">
                          <Mail className="h-5 w-5 mr-2 text-[#7C8F75]" />
                          Contact Information
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                          <div className="space-y-2">
                            <Label htmlFor="name" className="text-[#333333] font-medium">Full Name</Label>
                            <Input
                              id="name"
                              type="text"
                              value={formData.name}
                              onChange={(e) => handleInputChange('name', e.target.value)}
                              className="h-12 border-[#7C8F75]/30 focus:border-[#7C8F75] focus:ring-[#7C8F75]/20 rounded-xl"
                              placeholder="Enter your full name"
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="email" className="text-[#333333] font-medium">Email Address</Label>
                            <Input
                              id="email"
                              type="email"
                              value={formData.email}
                              onChange={(e) => handleInputChange('email', e.target.value)}
                              className="h-12 border-[#7C8F75]/30 focus:border-[#7C8F75] focus:ring-[#7C8F75]/20 rounded-xl"
                              placeholder="Enter your email"
                              required
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="phone" className="text-[#333333] font-medium">Phone Number</Label>
                          <Input
                            id="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            className="h-12 border-[#7C8F75]/30 focus:border-[#7C8F75] focus:ring-[#7C8F75]/20 rounded-xl"
                            placeholder="Enter your phone number"
                            required
                          />
                        </div>
                      </div>

                      {/* Special Requests */}
                      <div className="bg-white/70 p-4 sm:p-6 rounded-2xl border border-[#7C8F75]/20 shadow-sm">
                        <h4 className="text-lg sm:text-xl font-semibold text-[#333333] mb-4">Special Requests</h4>
                        <Textarea
                          id="message"
                          value={formData.message}
                          onChange={(e) => handleInputChange('message', e.target.value)}
                          className="border-[#7C8F75]/30 focus:border-[#7C8F75] focus:ring-[#7C8F75]/20 rounded-xl"
                          placeholder="Any special requests, dietary requirements, or questions?"
                          rows={4}
                        />
                      </div>

                      <Button
                        type="submit"
                        size="lg"
                        disabled={dateError !== ''}
                        className="w-full bg-gradient-to-r from-[#7C8F75] to-[#A8B89A] hover:from-[#7C8F75]/90 hover:to-[#A8B89A]/90 text-white py-4 sm:py-6 text-lg sm:text-xl font-semibold transform hover:scale-105 transition-all duration-300 shadow-xl rounded-2xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                      >
                        Reserve Your Mountain View Experience
                      </Button>
                    </form>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar - Takes 1 column */}
            <div className="space-y-6 sm:space-y-8">
              {/* Contact Card */}
              <div className="bg-gradient-to-br from-[#7C8F75] to-[#A8B89A] p-6 sm:p-8 rounded-3xl shadow-2xl text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-16 h-16 sm:w-24 sm:h-24 bg-white/10 rounded-full -translate-y-8 translate-x-8 sm:-translate-y-12 sm:translate-x-12"></div>
                <div className="absolute bottom-0 left-0 w-12 h-12 sm:w-16 sm:h-16 bg-white/10 rounded-full translate-y-6 -translate-x-6 sm:translate-y-8 sm:-translate-x-8"></div>
                
                <div className="relative z-10">
                  <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Need Immediate Help?</h3>
                  <p className="mb-4 sm:mb-6 opacity-90 text-sm sm:text-base">
                    Our team is here to assist you with your booking and answer any questions.
                  </p>
                  <div className="space-y-4">
                    {contactInfo.map((contact, index) => (
                      <a
                        key={index}
                        href={contact.href}
                        className="flex items-center space-x-4 p-4 bg-white/20 backdrop-blur-sm rounded-2xl hover:bg-white/30 transition-all duration-300 transform hover:scale-105"
                      >
                        <div className="bg-white/20 p-3 rounded-xl">
                          <contact.icon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <div className="text-sm opacity-80">{contact.label}</div>
                          <div className="font-semibold">{contact.value}</div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              {/* Highlights */}
              <div className="bg-white p-6 sm:p-8 rounded-3xl shadow-xl border border-[#7C8F75]/10">
                <h3 className="text-xl sm:text-2xl font-bold text-[#333333] mb-4 sm:mb-6">Why Choose Mountain View?</h3>
                <div className="space-y-4">
                  {highlights.map((highlight, index) => (
                    <div key={index} className="flex items-center space-x-4 p-4 bg-[#F9F6F2] rounded-2xl hover:bg-[#7C8F75]/10 transition-all duration-300">
                      <div className="bg-gradient-to-r from-[#7C8F75] to-[#A8B89A] p-2 rounded-xl">
                        <highlight.icon className="h-5 w-5 text-white" />
                      </div>
                      <span className="font-medium text-[#333333] text-sm sm:text-base">{highlight.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Booking Info */}
              <div className="bg-[#F9F6F2] p-6 sm:p-8 rounded-3xl border border-[#7C8F75]/10">
                <h3 className="text-xl sm:text-2xl font-bold text-[#333333] mb-4 sm:mb-6">Booking Information</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Calendar className="h-5 w-5 text-[#7C8F75] mt-1" />
                    <div>
                      <h4 className="font-semibold text-[#333333] text-sm sm:text-base">Check-in/Check-out</h4>
                      <p className="text-[#333333]/70 text-sm">Check-in: 3:00 PM | Check-out: 11:00 AM</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Users className="h-5 w-5 text-[#7C8F75] mt-1" />
                    <div>
                      <h4 className="font-semibold text-[#333333] text-sm sm:text-base">Capacity</h4>
                      <p className="text-[#333333]/70 text-sm">Up to 15 guests comfortably</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <DollarSign className="h-5 w-5 text-[#7C8F75] mt-1" />
                    <div>
                      <h4 className="font-semibold text-[#333333] text-sm sm:text-base">Flexible Pricing</h4>
                      <p className="text-[#333333]/70 text-sm">Competitive rates available</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Wave transition to Contact section */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none z-20">
        <svg 
          className="relative block w-full h-32" 
          data-name="Layer 1" 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 1200 120" 
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="bookingWaveGradient1" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
              <stop offset="20%" stopColor="rgba(255, 255, 255, 0.9)" stopOpacity="0.9" />
              <stop offset="40%" stopColor="rgba(249, 246, 242, 0.7)" stopOpacity="0.7" />
              <stop offset="60%" stopColor="rgba(249, 246, 242, 0.8)" stopOpacity="0.8" />
              <stop offset="80%" stopColor="rgba(249, 246, 242, 0.95)" stopOpacity="0.95" />
              <stop offset="100%" stopColor="#F9F6F2" stopOpacity="1" />
            </linearGradient>
            <linearGradient id="bookingWaveGradient2" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(255, 255, 255, 0.8)" stopOpacity="0.8" />
              <stop offset="30%" stopColor="rgba(124, 143, 117, 0.05)" stopOpacity="0.3" />
              <stop offset="60%" stopColor="rgba(232, 227, 221, 0.6)" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#E8E3DD" stopOpacity="1" />
            </linearGradient>
            <linearGradient id="bookingWaveGradient3" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(255, 255, 255, 0.6)" stopOpacity="0.6" />
              <stop offset="50%" stopColor="rgba(249, 246, 242, 0.9)" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#F9F6F2" stopOpacity="1" />
            </linearGradient>
          </defs>
          
          {/* Base wave */}
          <path 
            d="M0,60L48,55C96,50,192,40,288,45C384,50,480,70,576,75C672,80,768,70,864,60C960,50,1056,40,1152,45C1200,50,1248,70,1296,75L1344,80L1344,120L1296,120C1248,120,1152,120,1056,120C960,120,864,120,768,120C672,120,576,120,480,120C384,120,288,120,192,120C96,120,48,120,24,120L0,120Z" 
            fill="url(#bookingWaveGradient1)"
          />
          
          {/* Second wave layer */}
          <path 
            d="M0,40L48,35C96,30,192,20,288,25C384,30,480,50,576,55C672,60,768,50,864,40C960,30,1056,20,1152,25C1200,30,1248,50,1296,55L1344,60L1344,120L1296,120C1248,120,1152,120,1056,120C960,120,864,120,768,120C672,120,576,120,480,120C384,120,288,120,192,120C96,120,48,120,24,120L0,120Z" 
            fill="url(#bookingWaveGradient2)"
          />
          
          {/* Final wave */}
          <path 
            d="M0,20L48,15C96,10,192,0,288,5C384,10,480,30,576,35C672,40,768,30,864,20C960,10,1056,0,1152,5C1200,10,1248,30,1296,35L1344,40L1344,120L1296,120C1248,120,1152,120,1056,120C960,120,864,120,768,120C672,120,576,120,480,120C384,120,288,120,192,120C96,120,48,120,24,120L0,120Z" 
            fill="url(#bookingWaveGradient3)"
          />
        </svg>
      </div>
    </section>
  );
};

export default Booking;



